/** @file Model for the `calendar_config` user preference. */
import weekData from 'cldr-core/supplemental/weekData.json';
import { Codec, Left, number, Right } from 'purify-ts';
import { assert } from '@codegrade/utils';
import { UIPrefsStore } from '@/store';
import * as cacheUtils from '@/utils/cache';
/** The codec for parsing the raw JSON value. */
const dayOfWeekCodec = Codec.custom({
    encode: number.encode,
    decode: (value) => number.decode(value).chain(n => {
        switch (n) {
            case 1:
            case 2:
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
                return Right(n);
            default:
                return Left('expected a number between 1 and 7 (inclusive)');
        }
    }),
});
/** Lookup from day names used in cldr-core to data for that day. */
export const WEEKDAY_LOOKUP = Object.freeze({
    mon: Object.freeze({ order: 1, value: 1, name: 'Monday' }),
    tue: Object.freeze({ order: 2, value: 2, name: 'Tuesday' }),
    wed: Object.freeze({ order: 3, value: 3, name: 'Wednesday' }),
    thu: Object.freeze({ order: 4, value: 4, name: 'Thursday' }),
    fri: Object.freeze({ order: 5, value: 5, name: 'Friday' }),
    sat: Object.freeze({ order: 6, value: 6, name: 'Saturday' }),
    sun: Object.freeze({ order: 0, value: 7, name: 'Sunday' }),
});
/**
 * Get the index of the requested day in the week.
 *
 * @param day The day to get the index of.
 * @returns The index of the requested day, or `undefined` if the day is invalid.
 */
function getDayIndex(day) {
    if (day != null && Object.hasOwn(WEEKDAY_LOOKUP, day)) {
        return WEEKDAY_LOOKUP[day].value;
    }
    return undefined;
}
/**
 * Get the first day of the week based on the user's locale.
 *
 * @returns The first day of the week.
 */
function getDefaultFirstDayOfWeek() {
    const intlLanguage = new Intl.DateTimeFormat()?.resolvedOptions()?.locale;
    const languages = intlLanguage == null ? navigator.languages ?? [] : [intlLanguage];
    // Intl.Locale::getWeekInfo is not yet supported on Firefox.
    if (typeof Intl.Locale.prototype.getWeekInfo === 'function') {
        for (const lang of languages) {
            const loc = new Intl.Locale(lang);
            // @ts-expect-error The es2020 types we have do not include `getWeekInfo`.
            const firstDay = loc.getWeekInfo()?.firstDay;
            if (firstDay != null) {
                return firstDay;
            }
        }
    }
    const firstDays = weekData.supplemental.weekData.firstDay;
    for (const lang of languages) {
        const region = new Intl.Locale(lang).region;
        if (region != null && Object.hasOwn(firstDays, region)) {
            const firstDay = getDayIndex(firstDays[region]);
            if (firstDay != null) {
                return firstDay;
            }
        }
    }
    // Defaults to Sunday as that is what we used to do before these settings were created.
    return WEEKDAY_LOOKUP.sun.value;
}
/**
 * The `calendar_config` user preference model.
 *
 * This namespace contains general information about the preference, as well as
 * functions for getting and setting the preferred value.
 */
export var CalendarConfig;
(function (CalendarConfig) {
    /** The name of the user preference. */
    CalendarConfig.PREF_NAME = 'calendar_config';
    /** The codec to use for this preference. */
    CalendarConfig.codec = Codec.interface({
        firstDayOfWeek: dayOfWeekCodec,
    });
    /**
     * Cache for the default value so we don't have to recompute it every time.
     *
     * We cannot simply calculate the default value when this namespace is
     * created, because it depends on the site settings, so we must be sure
     * those have been loaded before the default is computed.
     */
    const cache = cacheUtils.makeCache('defaultValue');
    /**
     * @returns The default value used if the value received from the backend is
     * invalid, or if no value was present at all.
     */
    function getDefaultValue() {
        return cache.get('defaultValue', () => ({
            firstDayOfWeek: getDefaultFirstDayOfWeek(),
        }));
    }
    CalendarConfig.getDefaultValue = getDefaultValue;
    /** @returns A `Promise` that resolves with the value stored for the logged in user. */
    function load() {
        return UIPrefsStore.loadUIPreference({
            preference: CalendarConfig.PREF_NAME,
        });
    }
    CalendarConfig.load = load;
    /**
     * Store a new value for the logged in user.
     *
     * @param value The new value to store.
     * @returns A `Promise` that resolves when the value was successfully saved.
     */
    function set(value) {
        return UIPrefsStore.patchUIPreference({
            preference: CalendarConfig.PREF_NAME,
            value,
        });
    }
    CalendarConfig.set = set;
    /** @returns The current value of the preference for the logged in user. */
    function get() {
        const pref = UIPrefsStore.getUIPref()(CalendarConfig.PREF_NAME, CalendarConfig.codec);
        const status = pref.status;
        switch (status) {
            case 'not-loaded':
            case 'not-present':
            case 'not-valid':
                return getDefaultValue();
            case 'valid':
                return pref.value;
            default:
                throw assert.AssertionError.assertNever(status);
        }
    }
    CalendarConfig.get = get;
})(CalendarConfig || (CalendarConfig = {}));
